import { useTokens } from '@frontend/shared';
import { Spinner } from '@frontend/shared/web';
import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';

import { AccessDeniedPanel } from '../components/layout/AccessDeniedPanel';
import {
  EXTRANET_ROLES,
  firstAccessiblePath,
  rolesOf,
  unionScopes,
  type ExtranetPermission,
  type ExtranetRole,
  type RoleGrant,
  type RoleScope,
} from '../domain/access';
import { getHttpStatus, hasBackError } from '../lib/apiError';
import { decodeJwt, splitFullName } from '../lib/jwt';
import { endSession } from '../lib/session';
import { useMeAccess } from '../services/api/hooks/useMeAccess';
import type { AccountFilterOutput } from '../types/extranet-api.types';

/** Identité de l'appelant, lue dans les claims du JWT (pas d'email disponible). */
export interface AccountMe {
  id: string;
  userKey: string;
  accountKey: string;
  fullName: string;
  firstName: string;
  lastName: string;
}

interface AccountContextValue {
  me: AccountMe;
  /** Permissions de l'appelant — **la seule** source du gating. */
  permissions: ReadonlySet<string>;
  /** true si l'appelant possède cette permission. */
  can: (permission: ExtranetPermission) => boolean;
  isAdmin: boolean;
  /**
   * Rôles et périmètre du membre, pour l'affichage et la gestion. Ne **pas**
   * s'en servir pour décider ce que l'appelant peut faire : c'est `can()`.
   */
  access: RoleGrant[];
  /**
   * Facettes autorisées pour une permission — à utiliser comme filtres sur
   * l'écran de ce domaine. Ce sont exactement les valeurs que la route
   * acceptera.
   */
  filtersFor: (permission: ExtranetPermission) => AccountFilterOutput[];
  /**
   * Rôles que l'appelant peut attribuer. Un admin les attribue tous ; tout
   * autre membre ne peut inviter que sur ses **propres** rôles.
   */
  grantableRoles: ExtranetRole[];
  /**
   * Périmètre attribuable avec ce rôle, plafonné par celui de l'appelant. Vient
   * de `/me.access` et non de `/me.filters` : toutes les dimensions de filtrage
   * ne sont pas des périmètres (cf. `unionScopes`).
   */
  grantableScopesFor: (role: ExtranetRole) => RoleScope[];
  /** true si l'appelant a au moins un rôle à déléguer, donc peut inviter. */
  canInvite: boolean;
}

const AccountContext = createContext<AccountContextValue | null>(null);

interface AccountProviderProps {
  children: ReactNode;
  /** Rendu quand `/v1/users/me` échoue pour une raison autre qu'un refus d'accès (réseau, 5xx). */
  renderError: (retry: () => void) => ReactNode;
}

/** 401, ou `INVALID_TOKEN` quel que soit le statut : la session est morte. */
const isSessionError = (err: unknown): boolean => getHttpStatus(err) === 401 || hasBackError(err, 'INVALID_TOKEN');

/**
 * Charge le contexte de l'appelant. `/v1/users/me` est **bloquant** : le JWT ne
 * porte pas les permissions extranet, rien ne peut être dérivé avant la réponse.
 *
 * - session morte (claims indécodables, 401, `INVALID_TOKEN`) → purge, et
 *   `AuthenticatedLayout` redirige vers `/login` ;
 * - 403 (session non-extranet, membre inactif, droits révoqués) → panneau
 *   « Accès refusé » avec déconnexion, sans boucle ;
 * - aucune permission ouvrant un écran → même panneau ;
 * - autre erreur → `renderError` avec un bouton « Réessayer ».
 */
export const AccountProvider = ({ children, renderError }: AccountProviderProps) => {
  const { tokens } = useTokens();
  const claims = useMemo(() => decodeJwt(tokens?.accessToken), [tokens?.accessToken]);

  const meQ = useMeAccess();

  const mustEndSession = claims === null || isSessionError(meQ.error);
  useEffect(() => {
    if (mustEndSession) void endSession();
  }, [mustEndSession]);

  if (mustEndSession || !claims) return null;

  if (meQ.isPending) return <Spinner />;

  if (meQ.isError) {
    if (getHttpStatus(meQ.error) === 403) {
      return (
        <AccessDeniedPanel description="Votre compte n'a pas accès au portail extranet. Contactez votre administrateur Allocab." />
      );
    }
    return <>{renderError(() => void meQ.refetch())}</>;
  }

  const permissions = new Set(meQ.data.permissions ?? []);
  const filterEntries = meQ.data.filters ?? [];

  // `extranet-access` seule n'ouvre aucun écran : sans permission de domaine,
  // le portail n'aurait rien à afficher.
  if (firstAccessiblePath(permissions) === null) {
    return (
      <AccessDeniedPanel description="Aucun droit n'est associé à votre compte. Contactez votre administrateur Allocab." />
    );
  }

  const access = meQ.data.access ?? [];
  const isAdmin = permissions.has('extranet-admin');
  // Un admin attribue les 4 profils ; les autres membres ne délèguent que ce
  // qu'ils portent eux-mêmes. Le périmètre suit la même règle : plafonné par
  // l'union des leurs pour un admin, par celui du rôle concerné sinon — sans
  // quoi un membre restreint sur un rôle pourrait s'affranchir de sa limite en
  // invitant sur un autre.
  const ownScopes = unionScopes(access);

  const value: AccountContextValue = {
    me: {
      id: claims.id,
      userKey: claims.userKey,
      accountKey: claims.accountKey,
      fullName: claims.userFullName,
      ...splitFullName(claims.userFullName),
    },
    permissions,
    can: permission => permissions.has(permission),
    isAdmin,
    access,
    filtersFor: permission => filterEntries.find(entry => entry.permission === permission)?.filters ?? [],
    grantableRoles: isAdmin ? [...EXTRANET_ROLES] : rolesOf(access),
    grantableScopesFor: role => (isAdmin ? ownScopes : (access.find(grant => grant.role === role)?.scopes ?? [])),
    canInvite: isAdmin || rolesOf(access).length > 0,
  };

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
};

export const useAccount = (): AccountContextValue => {
  const ctx = useContext(AccountContext);
  if (!ctx) {
    throw new Error("useAccount doit être utilisé à l'intérieur d'<AccountProvider>");
  }
  return ctx;
};
